import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { RoundedBox } from "@react-three/drei";
import * as THREE from "three";
import { drawFace, FACE_H, FACE_W } from "./faceTexture";
import { hashString, rng } from "./layout";
import { surface } from "./textures";
import { FLOWER_COLORS } from "../lib/constants";

const geo = {};

function classicGeometry() {
  if (geo.classic) return geo.classic;
  const s = new THREE.Shape();
  s.moveTo(-0.55, 0);
  s.lineTo(0.55, 0);
  s.lineTo(0.55, 0.9);
  s.absarc(0, 0.9, 0.55, 0, Math.PI, false);
  s.lineTo(-0.55, 0);
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.22, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.03, bevelSegments: 3, curveSegments: 28 });
  g.translate(0, 0.12, -0.11);
  geo.classic = g;
  return g;
}

function naturalGeometry(variant) {
  const key = `natural${variant}`;
  if (geo[key]) return geo[key];
  const r = rng(1000 + variant * 77);
  const s = new THREE.Shape();
  s.moveTo(-0.62, 0);
  s.lineTo(0.62, 0);
  s.lineTo(0.6 + r() * 0.06, 0.45);
  s.lineTo(0.58 + r() * 0.05, 0.95);
  s.quadraticCurveTo(0.55, 1.3 + r() * 0.06, 0.25, 1.38 + r() * 0.06);
  s.lineTo(-0.1, 1.42 + r() * 0.04);
  s.quadraticCurveTo(-0.52, 1.36 + r() * 0.06, -0.6 - r() * 0.04, 1.0);
  s.lineTo(-0.63, 0.42);
  s.lineTo(-0.62, 0);
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.24, bevelEnabled: true, bevelThickness: 0.05, bevelSize: 0.05, bevelSegments: 2, curveSegments: 10 });
  g.translate(0, 0.12, -0.12);
  const pos = g.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const z = pos.getZ(i);
    if (Math.abs(z) > 0.1) pos.setZ(i, z + (Math.sin(pos.getX(i) * 9 + variant) * 0.012 + Math.cos(pos.getY(i) * 7) * 0.01) * Math.sign(z));
  }
  g.computeVertexNormals();
  geo[key] = g;
  return g;
}

const MATERIALS = {
  classic: () => new THREE.MeshStandardMaterial({ color: "#f4efe6", roughness: 0.55, ...surface("marble", [1, 1]), normalScale: new THREE.Vector2(0.6, 0.6) }),
  modern: () => new THREE.MeshPhysicalMaterial({ color: "#1b1c20", roughness: 0.18, metalness: 0.05, clearcoat: 1, clearcoatRoughness: 0.08 }),
  natural: () => new THREE.MeshStandardMaterial({ color: "#c4bdae", roughness: 0.95, ...surface("rock", [1, 1]) }),
};
const BASES = {
  classic: () => new THREE.MeshStandardMaterial({ color: "#ddd6c9", roughness: 0.7, ...surface("marble", [2, 1]) }),
  modern: () => new THREE.MeshStandardMaterial({ color: "#2b2c31", roughness: 0.3 }),
  natural: () => new THREE.MeshStandardMaterial({ color: "#9a9384", roughness: 1, ...surface("rock", [2, 1]) }),
};
const FACE = {
  classic: { y: 0.84, z: 0.142, glow: 0.12 },
  modern: { y: 0.895, z: 0.082, glow: 0.75 },
  natural: { y: 0.8, z: 0.2, glow: 0.3 },
};

let shadowTex;
function shadowTexture() {
  if (shadowTex) return shadowTex;
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 4, 64, 64, 62);
  g.addColorStop(0, "rgba(0,0,0,0.55)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  shadowTex = new THREE.CanvasTexture(c);
  return shadowTex;
}

function useFaceTexture(m, model) {
  const [tex, setTex] = useState(null);
  const key = `${m.name}|${m.surname}|${m.birth_date}|${m.death_date}|${m.epitaph}|${m.epitaph_font}|${m.photoUrl}|${model}`;
  useEffect(() => {
    let alive = true;
    const canvas = document.createElement("canvas");
    canvas.width = FACE_W;
    canvas.height = FACE_H;
    drawFace(canvas, m, model).then(() => {
      if (!alive) return;
      const t = new THREE.CanvasTexture(canvas);
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      setTex((old) => {
        old?.dispose();
        return t;
      });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => () => tex?.dispose(), [tex]);
  return tex;
}

const Flowers = ({ count }) => {
  const kinds = Object.keys(FLOWER_COLORS);
  const items = useMemo(() => {
    const r = rng(count * 31 + 7);
    return Array.from({ length: Math.min(count, 9) }, (_, i) => ({
      x: (r() - 0.5) * 1.1,
      z: 0.22 + r() * 0.2,
      h: 0.1 + r() * 0.08,
      tilt: (r() - 0.5) * 0.5,
      color: FLOWER_COLORS[kinds[i % kinds.length]],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  return items.map((f, i) => (
    <group key={i} position={[f.x, 0.12, f.z]} rotation={[f.tilt, 0, f.tilt * 0.6]}>
      <mesh position={[0, f.h / 2, 0]}>
        <cylinderGeometry args={[0.006, 0.008, f.h, 4]} />
        <meshStandardMaterial color="#56663f" />
      </mesh>
      <mesh position={[0, f.h, 0]}>
        <icosahedronGeometry args={[0.034, 0]} />
        <meshStandardMaterial color={f.color} roughness={0.7} />
      </mesh>
    </group>
  ));
};

export const Tombstone = ({ memorial, position = [0, 0, 0], rotationY = 0, onSelect, appear = false, showShadow = true, castShadow = true }) => {
  const model = memorial.model || "classic";
  const variant = useMemo(() => hashString(memorial.slug || memorial.name || "x") % 5, [memorial.slug, memorial.name]);
  const body = useMemo(() => MATERIALS[model](), [model]);
  const base = useMemo(() => BASES[model](), [model]);
  const tex = useFaceTexture(memorial, model);
  const face = FACE[model];
  const group = useRef();
  const faceMat = useRef();
  const start = useRef(null);
  const done = useRef(!appear);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (appear) {
      [body, base].forEach((mat) => {
        mat.transparent = true;
        mat.opacity = 0;
        mat.needsUpdate = true;
      });
    }
    return () => {
      body.dispose();
      base.dispose();
    };
  }, [body, base, appear]);

  useFrame((state) => {
    const g = group.current;
    if (!g) return;
    if (!done.current) {
      if (start.current === null) start.current = state.clock.elapsedTime;
      const t = Math.min(1, (state.clock.elapsedTime - start.current) / 2.6);
      const e = 1 - Math.pow(1 - t, 3);
      g.scale.setScalar(0.94 + 0.06 * e);
      body.opacity = e;
      base.opacity = e;
      if (faceMat.current) faceMat.current.opacity = e;
      if (t >= 1) {
        done.current = true;
        [body, base].forEach((mat) => {
          mat.transparent = false;
          mat.opacity = 1;
          mat.needsUpdate = true;
        });
      }
      return;
    }
    const target = hover ? 1.03 : 1;
    g.scale.setScalar(g.scale.x + (target - g.scale.x) * 0.12);
  });

  const handlers = onSelect
    ? {
        onClick: (e) => {
          e.stopPropagation();
          onSelect(memorial);
        },
        onPointerOver: (e) => {
          e.stopPropagation();
          setHover(true);
          document.body.style.cursor = "pointer";
        },
        onPointerOut: () => {
          setHover(false);
          document.body.style.cursor = "";
        },
      }
    : {};

  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <group ref={group} {...handlers}>
        {model === "modern" ? (
          <RoundedBox args={[1.05, 1.55, 0.16]} radius={0.025} smoothness={3} position={[0, 0.895, 0]} material={body} castShadow={castShadow} receiveShadow />
        ) : (
          <mesh geometry={model === "classic" ? classicGeometry() : naturalGeometry(variant)} material={body} castShadow={castShadow} receiveShadow />
        )}
        <mesh position={[0, 0.06, 0.1]} material={base} castShadow={castShadow} receiveShadow>
          <boxGeometry args={[model === "natural" ? 1.5 : 1.42, 0.12, 0.72]} />
        </mesh>
        {tex && (
          <mesh position={[0, face.y, face.z]}>
            <planeGeometry args={[0.9, 1.23]} />
            <meshStandardMaterial ref={faceMat} map={tex} transparent roughness={0.85} emissive="#ffffff" emissiveMap={tex} emissiveIntensity={face.glow} depthWrite={false} polygonOffset polygonOffsetFactor={-2} />
          </mesh>
        )}
        {memorial.flowers_count > 0 && <Flowers count={memorial.flowers_count} />}
      </group>
      {showShadow && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, 0.15]}>
          <planeGeometry args={[2.6, 2.0]} />
          <meshBasicMaterial map={shadowTexture()} transparent depthWrite={false} />
        </mesh>
      )}
    </group>
  );
};
